import React, { useEffect, useRef, useState } from "react";
import { ProgressPill, shuffled } from "../../grammar/shared";
import { Prompt } from "./shared";

/**
 * BucketSortItem — one word at a time, tap the category it belongs to.
 * A wrong tap re-queues the word for another try (so every word ends up
 * sorted and heard). Correct when all sorted with at most one slip per four
 * words. Modeled on the grammar lessons' PersonSortGame.
 */
export default function BucketSortItem({ question, onAnswer, audio }) {
  const { buckets, items } = question.data;
  const [queue, setQueue] = useState(() => shuffled(items));
  const [sorted, setSorted] = useState(0);
  const [mistakes, setMistakes] = useState(0);
  const [feedback, setFeedback] = useState(null); // {correct, key}
  const reported = useRef(false);
  const current = queue[0] || null;

  useEffect(() => {
    if (current && audio.audioAllowed) audio.play(current.ar);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current && current.ar]);

  useEffect(() => {
    if (sorted >= items.length && !reported.current) {
      reported.current = true;
      onAnswer({ correct: mistakes <= Math.floor(items.length / 4), answer: { mistakes } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorted]);

  const pick = (bucket) => {
    if (!current || feedback) return;
    const correct = bucket.key === current.bucket;
    setFeedback({ correct, key: bucket.key });
    setTimeout(() => {
      setFeedback(null);
      if (correct) {
        setSorted((n) => n + 1);
        setQueue((q) => q.slice(1));
      } else {
        setMistakes((n) => n + 1);
        setQueue((q) => [...q.slice(1), q[0]]);
      }
    }, 650);
  };

  return (
    <>
      <div className="flex items-center justify-between gap-3 mb-3">
        <Prompt>{question.prompt_en}</Prompt>
        <ProgressPill>{sorted}/{items.length} sorted</ProgressPill>
      </div>
      {current && (
        <div className="flex flex-col items-center mb-5">
          <span
            dir="rtl" lang="ar"
            className={`font-arabic text-5xl leading-[1.6] px-8 pt-7 pb-3 rounded-2xl transition-all ${feedback && !feedback.correct ? "animate-pulse" : ""}`}
            style={{
              color: feedback ? (feedback.correct ? "var(--teal)" : "#e58c8c") : "var(--parchment)",
              background: "var(--ink)", border: "1px solid var(--panel-line)",
            }}
          >
            {current.ar}
          </span>
          {current.en && feedback?.correct && (
            <span className="text-sm mt-2" style={{ color: "var(--parchment-dim)" }}>{current.en}</span>
          )}
        </div>
      )}
      <div className={`grid gap-2.5 max-w-xl mx-auto ${buckets.length === 3 ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-2"}`}>
        {buckets.map((b) => {
          const isPicked = feedback && feedback.key === b.key;
          const border = isPicked ? (feedback.correct ? "1px solid var(--teal)" : "1px solid #dc5a5a") : "1px solid var(--panel-line)";
          return (
            <button key={b.key} type="button" onClick={() => pick(b)} disabled={!!feedback || !current}
                    className="rounded-xl px-4 py-4 text-center transition-all hover:brightness-110 disabled:cursor-default"
                    style={{ background: "var(--ink)", border }}>
              {b.label_ar && (
                <span dir="rtl" lang="ar" className="font-arabic text-2xl block" style={{ color: "var(--gold-bright)" }}>{b.label_ar}</span>
              )}
              <span className="text-sm block mt-1" style={{ color: "var(--parchment)" }}>{b.label_en}</span>
            </button>
          );
        })}
      </div>
    </>
  );
}
